import React, { useMemo, useState } from 'react';
import { Funnel, FunnelChart, ResponsiveContainer, Tooltip } from 'recharts';

import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';
import { prettySaltCategory } from '../../../utils/SaltCategoryLabels';
import {
    resolveDrivers,
    resolveMitigation,
    resolvePodDisclose,
    resolveAmplifiers,
} from '../../../data/saltProfileMarkers';
import {
    CHART_ACCENT_GOLD,
    CHART_ACCENT_RED,
    intensityHue,
    SEARED_TOOLTIP_CURSOR,
    SearedTooltip,
} from '../../common/cardStats/chartTheme.jsx';
import HeroPanel, { HeroPanelTotal } from '../../common/heroPanel/HeroPanel';
import './saltBreakdown.css';
import './saltPane.css';
// Reuse the .powerSection--collapsible / .powerSectionToggle styling defined
// alongside the Power Levels pane so the Salt profile collapsible matches the
// Deck Fingerprint panel without duplicating CSS.
import './PowerLevelPane.css';

// Categories whose `list` entries are individual cards (keyed by card id)
// vs. derived aggregates like infiniteCombos / locks which key by combo id
// and don't merge with the deck's `cards` map.
const CARD_BACKED_CATS = new Set([
    'mld',
    'boardwipes',
    'taxes',
    'cedhMetashare',
    'sacrifice',
    'reserved',
    'cantActivate',
    'cantAttack',
    'cantCast',
    'cantDraw',
    'cantEven',
    'cantGainLife',
    'cantSearch',
    'cantTrigger',
    'cantUntap',
    'edhrec',
    'theft',
    'extraCombats',
    'extraTurns',
    'wheels',
    'offColorFetches',
    'cardPrice',
    'payTheOne',
    'discard',
    'poison',
    'chaos',
    'groupSlug',
    'annihilator',
]);

function formatScore(n, digits = 1) {
    if (!Number.isFinite(n)) return '—';
    return n.toFixed(digits);
}

const prettyCategoryLabel = prettySaltCategory;

/**
 * Consolidated salt profile. Single collapsible-free panel that renders
 * the personality treatment (top) and then the whole "what makes this
 * deck salty" surface: rage meter, category chips (from `markers[]`),
 * drivers, mitigation, pod disclosure, amplifiers, and top / stackable
 * offenders. Consumes `profile` = `deck.details.salt.profile` when the
 * backend has emitted one; otherwise falls back to deriving what it can
 * from the raw `categories` map.
 */
export function SaltBreakdown({ categories, deckTotal, personality, profile, saltPercentage }) {
    const openCardInfo = useOpenCardInfo();

    const [auditOpen, setAuditOpen] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
        return window.matchMedia('(min-width: 768px)').matches;
    });

    const { categoryStats, topOffenders } = useMemo(
        () => buildSaltModel(categories),
        [categories]
    );

    const totalSalt = useMemo(() => {
        if (Number.isFinite(deckTotal) && deckTotal > 0) return deckTotal;
        if (Number.isFinite(profile?.headline?.total) && profile.headline.total > 0) {
            return profile.headline.total;
        }
        return categoryStats.reduce((acc, c) => acc + c.total, 0);
    }, [deckTotal, profile, categoryStats]);

    // Chef's-kiss funnel data — ranked list of categories by raw salt
    // score, tinted from saltiness red at the head down to chiller golds
    // at the tip. Mirrors pre-profile behavior exactly.
    const chartData = useMemo(() => {
        const saltiness = saltPercentage || 0;
        const rows = [];
        Object.keys(categories || {}).forEach((catId) => {
            const cat = categories[catId];
            if (!cat || cat.noChart) return;
            const items = cat.list || [];
            if (items.length === 0) return;
            const subTotal = items.reduce((acc, cur) => acc + Number(cur?.score || 0), 0);
            rows.push({
                value: subTotal,
                label: cat.chartLabel || cat.label,
                name: cat.chartLabel || cat.label,
            });
        });
        rows.sort((a, b) => b.value - a.value);
        const stepRange = Math.max(20, Math.min(70, 100 - saltiness));
        const step = rows.length > 1 ? stepRange / (rows.length - 1) : 0;
        return rows.map((row, idx) => {
            const hue = intensityHue(saltiness - idx * step);
            return { ...row, fill: `hsl(${hue} 85% 50%)` };
        });
    }, [categories, saltPercentage]);

    const drivers = useMemo(() => resolveDrivers(profile?.drivers), [profile]);
    const mitigation = useMemo(() => resolveMitigation(profile?.mitigation), [profile]);
    const podDisclose = useMemo(() => resolvePodDisclose(profile?.podDisclose), [profile]);
    const amplifiers = useMemo(() => resolveAmplifiers(profile?.amplifiers), [profile]);

    if (!categoryStats.length) return null;

    // "Drop top N" — what % of salt disappears if you swap out the top
    // three contributors? Clamps N at whatever's available.
    const topN = Math.min(3, topOffenders.length);
    const swapSavings = topOffenders.slice(0, topN).reduce((acc, c) => acc + c.total, 0);
    const swapPercent = totalSalt > 0 ? (swapSavings / totalSalt) * 100 : 0;

    return (
        <div className="saltBreakdown">
            {personality && (
                <PersonalityPanel
                    personality={personality}
                    totalSalt={totalSalt}
                    onSelectCard={openCardInfo}
                />
            )}

            <section
                className={`saltProfilePanel saltProfilePanel--collapsible${auditOpen ? ' is-open' : ''}`}
            >
                <button
                    type="button"
                    className="powerSectionToggle"
                    aria-expanded={auditOpen}
                    aria-controls="saltProfileBody"
                    onClick={() => setAuditOpen((v) => !v)}
                >
                    <span className="powerSectionEyebrow">
                        <span className="powerSectionChevron" aria-hidden="true">
                            {auditOpen ? '▾' : '▸'}
                        </span>
                        Salt profile
                    </span>
                    <span className="powerSectionHint">
                        How loud the rage meter reads and which categories drive the
                        score. Tap to expand the breakdown.
                    </span>
                </button>

                {auditOpen && (
                    <div id="saltProfileBody" className="saltProfilePanelBody">
                        <SaltinessAudit
                            chartData={chartData}
                            saltPercentage={saltPercentage}
                        />

                        {drivers.length > 0 && (
                            <ProfileMarkerList
                                title="Why this reads salty"
                                hint="Top signals the scoring engine is reacting to."
                                entries={drivers}
                                variant="drivers"
                            />
                        )}

                        {mitigation.length > 0 && (
                            <ProfileMarkerList
                                title="How to mellow it out"
                                hint="Where the biggest cuts are if you want less salt."
                                entries={mitigation}
                                variant="mitigation"
                            />
                        )}

                        {podDisclose.length > 0 && (
                            <ProfileMarkerList
                                title="Tell the pod first"
                                hint="Rule-zero / pre-game callouts the table will want to hear."
                                entries={podDisclose}
                                variant="disclose"
                                compact
                            />
                        )}

                        {amplifiers.length > 0 && (
                            <ProfileMarkerList
                                title="Worse than the sum of its parts"
                                hint="Combinations that feel saltier than each piece alone."
                                entries={amplifiers}
                                variant="amplifiers"
                                compact
                            />
                        )}

                        <SwapImpactPanel
                            topN={topN}
                            savings={swapSavings}
                            percent={swapPercent}
                            cards={topOffenders.slice(0, topN)}
                        />
                    </div>
                )}
            </section>
        </div>
    );
}

// ---------- Data derivation ----------------------------------------------

function buildSaltModel(categories) {
    const cardSalt = new Map();
    const categoryStats = [];
    const totalsByCat = {};

    const ingestCardList = (catId, list) => {
        for (const entry of list) {
            const id = entry?.id;
            if (!id) continue;
            const score = Number(entry?.score || 0);
            if (!score) continue;
            const existing = cardSalt.get(id);
            if (existing) {
                existing.total += score;
                existing.breakdown.push({ category: catId, score });
            } else {
                cardSalt.set(id, {
                    id,
                    name: entry.name || id,
                    imageUri: entry.imageUri,
                    types: entry.types,
                    isFrontFace: entry.isFrontFace,
                    total: score,
                    breakdown: [{ category: catId, score }],
                });
            }
        }
    };

    Object.keys(categories || {}).forEach((catId) => {
        const cat = categories[catId];
        const list = cat?.list || [];
        const total = list.reduce((acc, entry) => acc + Number(entry?.score || 0), 0);
        totalsByCat[catId] = total;

        if (list.length) {
            // Top single contributor for this category — the name we
            // surface in the stat tile so at-a-glance you see the
            // dominant card.
            let topEntry = null;
            for (const entry of list) {
                if (!topEntry || Number(entry?.score || 0) > Number(topEntry?.score || 0))
                    topEntry = entry;
            }

            categoryStats.push({
                id: catId,
                label: cat?.label || prettyCategoryLabel(catId),
                count: list.length,
                total,
                topEntry,
                isCardBacked: CARD_BACKED_CATS.has(catId),
            });

            if (CARD_BACKED_CATS.has(catId)) ingestCardList(catId, list);
        }

        // Stax sub-categories live under categories.cant.subCategories.*;
        // they're never top-level keys but their cards need to contribute
        // to topOffenders / stackable under their own category id.
        const subCats = cat?.subCategories || {};
        Object.keys(subCats).forEach((subId) => {
            const subList = subCats[subId]?.list || [];
            if (!subList.length) return;
            totalsByCat[subId] = subList.reduce((acc, e) => acc + Number(e?.score || 0), 0);
            if (CARD_BACKED_CATS.has(subId)) ingestCardList(subId, subList);
        });
    });

    categoryStats.sort((a, b) => b.total - a.total);

    const topOffenders = Array.from(cardSalt.values())
        .map((c) => ({ ...c, breakdown: c.breakdown.slice().sort((a, b) => b.score - a.score) }))
        .sort((a, b) => b.total - a.total);

    return { categoryStats, topOffenders };
}

// For a marker from the API, find the richer top-entry in the client-side
// `categories` map so we can link the "TOP" button to the card-info dialog
// (which needs imageUri/types that markers.topCard does not include).
// Stax sub-categories (cantDraw, cantUntap, …) live under the parent
// `cant` key's subCategories map, so fall back to that path when the
// marker key isn't a top-level entry.
// ---------- UI fragments --------------------------------------------------

function SaltinessAudit({ chartData, saltPercentage }) {
    const pct = Number.isFinite(saltPercentage) ? saltPercentage : 0;
    const intensityAngle = Math.max(0, Math.min(360, (pct / 100) * 360));
    const endGameEmojis = (() => {
        if (pct > 90) return '🤬 🤬 🤬';
        if (pct > 80) return '😡 😡 😡';
        if (pct > 70) return '🤯 🤯 🤯';
        if (pct > 60) return '😤 😤 😤';
        if (pct > 50) return '😭 😭 😭';
        if (pct > 40) return '😢 😢 😢';
        if (pct > 30) return '🤨 🤨 🤨';
        if (pct > 20) return '😒 😒 😒';
        if (pct > 10) return '🥹 🥹 🥹';
        return '😃 😃 😃';
    })();

    return (
        <div className="saltNailPanel">
            <div className="saltNailHeader">
                <span className="saltNailEyebrow">Saltiness Audit</span>
                <span className="saltNailTitle">How are we going to lose friends today?</span>
            </div>

            <div className="saltNailIntensityRow">
                <span className="saltNailIntensityLabel">Rage meter</span>
                <div className="saltNailIntensityTrack">
                    <div
                        className="saltNailIntensityFill"
                        style={{
                            width: `${pct}%`,
                            background: `linear-gradient(90deg, ${CHART_ACCENT_GOLD} 0%, ${CHART_ACCENT_RED} 100%)`,
                        }}
                    />
                </div>
                <span className="saltNailIntensityValue">{pct.toFixed(1)}%</span>
            </div>

            {chartData.length > 0 && (
                <div className="saltNailStage">
                    <div
                        className="saltNailStrike"
                        style={{
                            background: `conic-gradient(from ${-intensityAngle / 2}deg at 50% 0%, rgba(204, 26, 26, 0.25) 0deg, rgba(204, 26, 26, 0.08) ${intensityAngle}deg, transparent ${intensityAngle + 20}deg)`,
                        }}
                    />
                    <div className="saltNailColumn">
                        <div className="saltNailHead">
                            <span>CHEF&apos;S KISS</span>
                            <img
                                src="/resources/chef-kiss-header.png"
                                alt=""
                                width="28"
                                height="28"
                            />
                        </div>
                        <div className="saltNailFunnelWrap">
                            <ResponsiveContainer
                                width="100%"
                                height={Math.max(220, chartData.length * 48)}
                            >
                                <FunnelChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                                    <Tooltip
                                        content={
                                            <SearedTooltip
                                                hideLabel
                                                valueFormatter={(v, entry) => {
                                                    const payload = entry?.payload || {};
                                                    const score = Number.isFinite(payload?.value)
                                                        ? payload.value.toFixed(1)
                                                        : v;
                                                    return `${payload?.label || entry?.name || ''} · ${score}`;
                                                }}
                                            />
                                        }
                                        cursor={SEARED_TOOLTIP_CURSOR}
                                    />
                                    <Funnel
                                        dataKey="value"
                                        data={chartData}
                                        isAnimationActive
                                        stroke="rgba(0, 0, 0, 0.6)"
                                        strokeWidth={1}
                                    />
                                </FunnelChart>
                            </ResponsiveContainer>
                            <div className="saltNailDrivenShadow" />
                        </div>
                        <div className="saltNailEmojis">
                            <img
                                src="/resources/chef-kiss-header.png"
                                alt=""
                                className="saltNailKissIcon"
                            />
                            <span>{endGameEmojis}</span>
                        </div>
                    </div>
                    <div className="saltNailLegend">
                        {chartData.map((row) => (
                            <div
                                key={`legend_${row.label}`}
                                className="saltNailLegendRow"
                                style={{ borderLeftColor: row.fill }}
                            >
                                <span className="saltNailLegendLabel">{row.label}</span>
                                <span className="saltNailLegendValue">
                                    {row.value.toFixed(1)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="saltNailFooter">
                <span className="saltNailPercent">{pct.toFixed(3)}%</span>
                <span className="saltNailFooterText">chance of enraging your play group.</span>
            </div>
        </div>
    );
}

function PersonalityPanel({ personality, totalSalt, onSelectCard }) {
    const archetypes = personality?.archetypes || [];
    const primary = archetypes.find((a) => a.tier === 'primary') || archetypes[0];
    if (!primary && !personality?.headline) return null;
    const secondaries = archetypes.filter((a) => a.tier === 'secondary');
    const intensity = personality.intensity || 'moderate';
    const headline = personality.headline ?? primary?.label;
    const headlineHint = personality.headlineHint ?? primary?.hint;
    // Backend evidence.topCards occasionally surfaces combo entries (names
    // joined by commas). Those aren't real cards — clicking them can't open
    // the card-info dialog cleanly — so drop them from the pill row.
    const topCards = (primary?.evidence?.topCards || []).filter(
        (c) => c && typeof c.name === 'string' && !c.name.includes(',')
    );
    const primaryAriaLabel = headlineHint ? `${headline}. ${headlineHint}` : headline;
    return (
        <HeroPanel
            tone="purple"
            className="saltPersonality"
            data-intensity={intensity}
            aria-label={primaryAriaLabel}
            eyebrow="Salt personality"
            title={headline}
            hint={headlineHint || undefined}
            aside={
                <>
                    <HeroPanelTotal label="Deck salt" value={formatScore(totalSalt, 1)} />
                    {secondaries.length > 0 && (
                        <div className="saltPersonalityTags">
                            {secondaries.map((p) => (
                                <span
                                    key={p.key}
                                    className={
                                        'saltPersonalityTag' +
                                        (p.labelVariant === 'extreme' ? ' saltPersonalityTag--extreme' : '')
                                    }
                                    data-family={p.family || 'mixed'}
                                    title={p.hint || p.label}
                                    aria-label={p.hint ? `${p.label}. ${p.hint}` : p.label}
                                >
                                    {p.label}
                                </span>
                            ))}
                        </div>
                    )}
                </>
            }
        >
            {topCards.length > 0 && (
                <div className="saltPersonalityTopCards">
                    {topCards.map((card) => {
                        const canClick = !!(card?.id && onSelectCard);
                        return (
                            <button
                                key={card.id || card.name}
                                type="button"
                                className="saltPersonalityTopCard"
                                onClick={() => canClick && onSelectCard(card)}
                                disabled={!canClick}
                                title={`${card.name} · ${formatScore(Number(card.score || 0), 2)} salt`}
                            >
                                <span className="saltPersonalityTopCardName">{card.name}</span>
                                <span className="saltPersonalityTopCardScore">
                                    {formatScore(Number(card.score || 0), 1)}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </HeroPanel>
    );
}

function ProfileMarkerList({ title, hint, entries, variant, compact = false }) {
    return (
        <section className={`saltMarkerPanel saltMarkerPanel--${variant}`}>
            <header className="saltMarkerPanelHead">
                <span className="saltMarkerPanelEyebrow">{title}</span>
                {hint && <span className="saltMarkerPanelHint">{hint}</span>}
            </header>
            <ul className={`saltMarkerList${compact ? ' saltMarkerList--compact' : ''}`}>
                {entries.map((e, idx) => (
                    <li
                        key={`${e.id}_${idx}`}
                        className={`saltMarkerItem${e.tone ? ` saltMarkerItem--${e.tone}` : ''}`}
                    >
                        <span className="saltMarkerTitle">{e.title}</span>
                        {e.body && <span className="saltMarkerBody">{e.body}</span>}
                    </li>
                ))}
            </ul>
        </section>
    );
}

function SwapImpactPanel({ topN, savings, percent, cards }) {
    if (topN === 0 || savings <= 0) return null;
    return (
        <section className="saltSwapPanel">
            <div className="saltSwapHeader">
                <span className="saltSwapEyebrow">Swap impact</span>
                <span className="saltSwapHint">Dropping your top offenders cuts this much salt.</span>
            </div>
            <div className="saltSwapBody">
                <div className="saltSwapStat">
                    <span className="saltSwapStatLabel">Drop top {topN}</span>
                    <span className="saltSwapStatValue">&minus;{percent.toFixed(1)}%</span>
                    <span className="saltSwapStatSub">({formatScore(savings, 1)} salt)</span>
                </div>
                <div className="saltSwapCards">
                    {cards.map((c, idx) => (
                        <span key={c.id} className="saltSwapCard">
                            <span className="saltSwapCardRank">{idx + 1}</span>
                            <span
                                className="saltSwapCardName"
                                style={{ color: c.isFrontFace === false ? '#fff49a' : '#ffffff' }}
                            >
                                {c.name}
                            </span>
                            <span className="saltSwapCardScore">&minus;{formatScore(c.total, 1)}</span>
                        </span>
                    ))}
                </div>
            </div>
        </section>
    );
}
